import { FieldProps } from 'app/frontend/base/components/form-fields/types'
import { SelectProps } from 'app/frontend/base/components/Select'
import { tileTypeToKey, useGetTileName } from './TileWrapper'
import OverviewTileType from 'app/frontend/types/custom/OverviewTileType'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { OverviewTilePosition } from 'app/frontend/types/custom'
import { getConfigFieldsByTileType } from '../config'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import { useFormContext, Path } from 'react-hook-form'
import { FormValues } from '../form'
import { useTranslation } from 'react-i18next'
import { useOverviewConfig } from '../context'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import { GameVersionCapability } from 'app/frontend/types/custom/GameVersion'

const MAIN_ONLY_TILES = ['Tile::OverviewTable', 'Tile::SidebarTiles']
const SIDEBAR_ONLY_TILES = ['Tile::RaidProgress']
const TILE_CAPABILITIES: Record<string, GameVersionCapability> = {
  'Tile::RaidProgress': 'raid_progress_tile',
  'Tile::GuildMessage': 'guild_message_tile',
  'Tile::EnchantAudit': 'audit_tiles',
  'Tile::OverviewTable': 'audit_tiles',
}

type Props = {
  position: OverviewTilePosition
  configName: string
} & FieldProps<string> &
  Partial<Omit<SelectProps, 'options' | 'value'>>

const TileSelect = (props: Props) => {
  const { setValue } = useFormContext<FormValues>()
  const { t } = useTranslation()
  const getTileName = useGetTileName()
  const { allowedTileTypes } = useOverviewConfig()
  const { difficulties, defaultDifficulty } = useDifficulties()
  const gameVersion = useGameVersion()
  const configFieldsByTileType = getConfigFieldsByTileType(
    difficulties,
    defaultDifficulty,
  )

  const availableTileTypes = allowedTileTypes.filter((type) => {
    const capability = TILE_CAPABILITIES[type]
    if (capability && !gameVersion.has(capability)) return false

    if (props.position === 'sidebar') {
      return !MAIN_ONLY_TILES.includes(type)
    }

    return !SIDEBAR_ONLY_TILES.includes(type)
  })

  return (
    <SelectField
      {...props}
      layout='stacked'
      optionLabelFn={getTileName}
      options={availableTileTypes}
      onChange={(value) => {
        const defaultConfigForNewTile =
          configFieldsByTileType[value as OverviewTileType]
            ?.flat()
            .map((config) => [config.name, config.default ?? ''])
            .concat([
              [
                'title',
                t(
                  `overview.tiles.${tileTypeToKey(
                    value as OverviewTileType,
                  )}.default-title`,
                ),
              ],
            ]) ?? []

        setValue(
          props.configName as Path<FormValues>,
          Object.fromEntries(defaultConfigForNewTile),
          { shouldDirty: true },
        )
      }}
      inModal
    />
  )
}

export default TileSelect
